import React from 'react';
import {
  fmtMoney, fmtPct, fmtRate, fmtMinutes, marginPct, asOf, sourceBadgeClass, num,
} from '../../utils/priceMath';
import { costAnalysisRows, effectivePriceSource, lineUnitCost, markupSourceLabel } from '../../utils/quotePricing';

// Per-line "Cost Analysis" for the Quote page (F08 / F37).
//
// Every dollar in the table is shown next to the numbers that produce it:
//   labour rows      minutes × quoted $/hr = price   |  minutes × true $/hr = cost
//   material row     cost × (1 + markup) = price, with the cut-sheet / cut-list /
//                    CWT source sentence the engine used
//   outsourced rows  vendor cost × (1 + markup) = price, plus WHICH markup the
//                    engine applied (`markup_source`: quote outsourced markup /
//                    process default / material markup fallback — F43, wave 2)
// Breakdowns saved before the engine recorded provenance render with the
// rates IMPLIED from price ÷ hours (flagged) or, when minutes were never
// stored, an explicit "n/a — priced before provenance was recorded".

const SOURCE_LABELS = {
  historical: 'historical', estimate: 'estimate', assembly_row: 'assembly row', vendor: 'vendor',
  theoretical_beam_on: 'theoretical (beam-on)', theoretical_total_time: 'theoretical (total time)', none: 'none',
};

const SourceBadge = ({ source, historyN }) => {
  if (!source) {
    return <span className="badge bg-light text-dark border" title="Source not recorded on this breakdown (priced before provenance was recorded)">n/a</span>;
  }
  const label = SOURCE_LABELS[source] || source;
  return (
    <span className={sourceBadgeClass(source)} title={`Hours / cost source: ${label}`}>
      {label}{source === 'historical' && historyN ? ` n=${historyN}` : ''}
    </span>
  );
};

/** F43 (wave 2): which markup the engine applied to an outsourced row. Absent on breakdowns stored before the engine recorded it. */
const MarkupSourceBadge = ({ source, markup }) => {
  const m = markupSourceLabel(source);
  if (!m) return null;
  const pct = Number.isFinite(markup) ? ` (${(markup * 100).toFixed(1)}%)` : '';
  return <span className="badge bg-light text-dark border ms-1" title={`Markup source${pct}: ${m.title}`}>{m.label}</span>;
};

const NA = ({ title }) => <span className="text-muted" title={title || 'n/a — priced before provenance was recorded'}>n/a</span>;

/** "25.0 min × $500.00/hr = $208.33" — or the bare dollar with a reason when the factors are unknown. */
const Product = ({ minutes, rate, amount, implied, rateNote }) => {
  const hasMin = Number.isFinite(minutes);
  const hasRate = Number.isFinite(rate);
  if (!Number.isFinite(amount)) return <NA />;
  if (!hasMin || !hasRate) {
    return (
      <span title={!hasMin ? 'Minutes were not recorded on this breakdown; only the dollar figure is stored.' : 'Rate not recorded on this breakdown.'}>
        {fmtMoney(amount)} <span className="text-muted small">(factors n/a)</span>
      </span>
    );
  }
  return (
    <span title={`${minutes.toFixed(3)} min ÷ 60 × ${fmtRate(rate)}${implied ? ' — rate implied from the stored dollar ÷ hours' : ''}${rateNote ? ` — ${rateNote}` : ''}`}>
      <span className="text-muted">{fmtMinutes(minutes)} × {fmtRate(rate)}{implied && <span className="fst-italic"> (implied)</span>} = </span>
      <strong>{fmtMoney(amount)}</strong>
    </span>
  );
};

const MarkupProduct = ({ cost, markup, price, implied }) => {
  if (!Number.isFinite(price)) return <NA />;
  if (!Number.isFinite(cost) || !Number.isFinite(markup)) return <span>{fmtMoney(price)}</span>;
  return (
    <span title={`${fmtMoney(cost)} × (1 + ${(markup * 100).toFixed(1)}%)${implied ? ' — markup implied from price ÷ cost' : ''}`}>
      <span className="text-muted">{fmtMoney(cost)} × {(1 + markup).toFixed(2)}{implied && <span className="fst-italic"> (implied)</span>} = </span>
      <strong>{fmtMoney(price)}</strong>
    </span>
  );
};

const InputsLine = ({ inputs }) => {
  if (!inputs) return null;
  const r = inputs.rates || {};
  const rateBits = [
    ['Laser', r.laser_rate], ['Tap', r.tap_rate], ['Brake', r.brake_rate],
    ['Fab', r.fabrication_rate], ['Blast', r.blast_rate], ['Finish', r.finish_rate],
  ].filter(([, v]) => v != null).map(([k, v]) => `${k} ${fmtRate(v)}`);
  return (
    <div className="text-muted small mb-2" style={{ lineHeight: 1.4 }}>
      <span title="Inputs the engine recorded when it priced this line">
        {inputs.engine_version != null && <>engine v{inputs.engine_version} · </>}
        {inputs.computed_at && <>{asOf(inputs.computed_at)} · </>}
        {rateBits.length > 0 && <>sell rates: {rateBits.join(', ')} · </>}
        material markup {fmtPct(inputs.material_markup, 0)}
        {inputs.coating_markup != null && <> · outsourced markup {fmtPct(inputs.coating_markup, 0)}</>}
        {inputs.true_rates_as_of && <> · true-cost rates {asOf(inputs.true_rates_as_of)}</>}
      </span>
    </div>
  );
};

const PriceOption = ({ id, name, label, price, cost, checked, disabled, onSelect, note, colorClass }) => {
  const usable = Number.isFinite(price) && price > 0;
  const profit = usable && Number.isFinite(cost) ? price - cost : null;
  const m = usable && Number.isFinite(cost) ? marginPct(cost, price) : null;
  return (
    <td className={`text-end align-top ${colorClass || ''}`} style={{ minWidth: '120px' }}>
      <div className="form-check form-check-inline m-0 p-0 d-flex justify-content-end align-items-center gap-1">
        <input className="form-check-input m-0" type="radio" id={id} name={name} checked={!!checked}
               disabled={disabled || !usable} onChange={() => usable && onSelect && onSelect()} />
        <label htmlFor={id} className="fw-bold mb-0" style={{ cursor: usable ? 'pointer' : 'default' }}>
          {usable ? fmtMoney(price) : '—'}
        </label>
      </div>
      <div className="small text-muted" style={{ lineHeight: 1.2 }}>{label}</div>
      {profit != null && (
        <div className="small" style={{ lineHeight: 1.2 }}>
          <span className="text-muted">{fmtMoney(profit)} · </span>
          <span className={m != null && m < 0.2 ? 'text-danger fw-bold' : 'text-success fw-bold'}>{fmtPct(m)}</span>
        </div>
      )}
      {note && <div className="small text-muted fst-italic" style={{ lineHeight: 1.2 }}>{note}</div>}
    </td>
  );
};

/**
 * props
 *   line            the quote line (price, price_source, price_basis, current_db_price, is_customer_supplied_material …)
 *   breakdown       line.cost_breakdown
 *   isOps           assembly-operations line
 *   header          {target_margin, material_markup} as fractions
 *   onSelectPrice   ({price, price_source, price_basis}) => void   (omit for read-only)
 *   tm              a Time & Materials quote line: no price basis to choose (the line is priced at
 *                   completion) — the chooser and the margin badge are replaced by a note; the cost
 *                   rows (the estimate) stay. A fixed-price line (tm false) renders exactly as before.
 */
export default function QuoteCostAnalysisTable({ line, breakdown, isOps = false, header, onSelectPrice, readOnly = false, tm = false }) {
  const a = costAnalysisRows(breakdown, { isOps });
  const rows = a.rows;
  const itemised = rows.filter(r => r.kind === 'material' || r.kind === 'labour' || r.kind === 'passthrough' || r.kind === 'outsourced');
  const sumCost = itemised.reduce((s, r) => s + (Number.isFinite(r.cost) ? r.cost : 0), 0);
  const sumPrice = itemised.reduce((s, r) => s + (Number.isFinite(r.price) ? r.price : 0), 0);
  const restCost = Number.isFinite(a.totalCost) ? a.totalCost - sumCost : NaN;
  const restPrice = Number.isFinite(a.calculatedPrice) ? a.calculatedPrice - sumPrice : NaN;
  const showRest = (Number.isFinite(restCost) && Math.abs(restCost) > 0.005) || (Number.isFinite(restPrice) && Math.abs(restPrice) > 0.005);

  const cost = lineUnitCost(line, breakdown);
  const current = num(line && line.price, NaN);
  const source = effectivePriceSource(line);
  const headerMargin = num(header && header.target_margin, NaN);
  const lineBasis = num(line && line.price_basis, NaN);
  // The header's target margin is always offered; when the line carries its
  // OWN target (e.g. 75% while the header says 40%) that basis is shown as a
  // separate, checked option so the operator can see it and move to the
  // header's margin with one click.
  const lineOwnTarget = source === 'target_margin' && Number.isFinite(lineBasis)
    && (!Number.isFinite(headerMargin) || Math.abs(lineBasis - headerMargin) > 1e-6);
  const targetM = headerMargin;
  const targetPrice = Number.isFinite(cost) && Number.isFinite(targetM) && targetM < 1 ? cost / (1 - targetM) : NaN;
  const ownTargetPrice = lineOwnTarget && Number.isFinite(cost) && lineBasis < 1 ? cost / (1 - lineBasis) : NaN;
  const bookPrice = num(line && line.current_db_price, NaN);
  const calcPrice = a.calculatedPrice;
  const currentMargin = Number.isFinite(cost) && current > 0 ? marginPct(cost, current) : null;
  const canSelect = !!onSelectPrice && !readOnly && !tm;
  const radioName = `priceBasis-${line && line.id}`;
  const pctLabel = (m) => `${(m * 100).toFixed(m * 100 % 1 === 0 ? 0 : 1)}%`;

  return (
    <div className="mt-2 p-3 border rounded shadow-sm" style={{ backgroundColor: 'var(--jq-bg-card, #fff)', fontSize: '0.85rem' }}>
      <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
        <h6 className="mb-0">Cost Analysis <span className="text-muted small fw-normal">(per unit{isOps ? ' — per set' : ''})</span></h6>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          {a.excludedSteps.length > 0 && (
            <span className="badge bg-info text-dark" title="These stations are priced on the assembly's ASSEMBLY OPS line, not on this part line.">
              priced on ops line: {a.excludedSteps.join(', ')}
            </span>
          )}
          {!tm && (
          <span className="badge bg-secondary" title={Number.isFinite(cost) ? `(price ${fmtMoney(current)} − cost ${fmtMoney(cost)}) ÷ price` : 'no cost'}>
            Current margin: {fmtPct(currentMargin)}
          </span>
          )}
        </div>
      </div>
      {a.provenanceNote ? (
        <div className="text-muted small mb-2 fst-italic">{a.provenanceNote}. Click Calc to re-price with provenance.</div>
      ) : <InputsLine inputs={a.inputs} />}

      <div className="table-responsive">
        <table className="table table-sm table-bordered mb-2 align-middle" style={{ fontSize: '0.82rem' }}>
          <thead className="table-light">
            <tr>
              <th style={{ width: '18%' }}>Item</th>
              <th style={{ width: '22%' }}>Source</th>
              <th className="text-end" style={{ width: '30%' }}>Quoted $/hr → Price</th>
              <th className="text-end text-muted" style={{ width: '30%' }}>True $/hr → Cost</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={4} className="text-muted fst-italic">No itemised breakdown stored on this line.</td></tr>
            )}
            {rows.map(r => {
              if (r.kind === 'material') {
                const csm = !!(line && line.is_customer_supplied_material);
                return (
                  <tr key={r.key}>
                    <td className="fw-bold">Material{csm && <span className="badge bg-warning text-dark ms-1" title="Customer supplied — material is charged at $0">cust. supplied</span>}</td>
                    <td>
                      {r.method && <span className="badge bg-light text-dark border me-1">{r.method}</span>}
                      {r.sourceKind && <span className={sourceBadgeClass(r.sourceKind === 'cut_sheet' || r.sourceKind === 'cut_list' ? 'historical' : 'estimate')}>{r.sourceKind}</span>}
                      {Number.isFinite(r.utilisation) && (
                        <span className={`badge ms-1 ${r.utilisation < 25 ? 'bg-warning text-dark' : 'bg-light text-dark border'}`} title="Share of the sheet / stick this part uses">{r.utilisation.toFixed(1)}% used</span>
                      )}
                      {r.description ? <div className="small text-muted" style={{ lineHeight: 1.2 }}>{r.description}</div>
                        : !r.method && <div className="small text-muted fst-italic">source n/a — priced before provenance was recorded</div>}
                    </td>
                    <td className="text-end"><MarkupProduct cost={r.cost} markup={r.markup} price={r.price} /></td>
                    <td className="text-end text-muted">{fmtMoney(r.cost, { dash: true })}</td>
                  </tr>
                );
              }
              if (r.kind === 'labour') {
                return (
                  <tr key={r.key}>
                    <td className="fw-bold">{r.label}</td>
                    <td>
                      <SourceBadge source={r.source} historyN={r.historyN} />
                      {r.isDefaultRate && <span className="badge bg-light text-dark border ms-1" title="No punches for this station in the last 90 days — true cost uses the default wage table">default rate</span>}
                      {r.trueRateSource === 'punch_weighted_90d' && <span className="badge bg-light text-dark border ms-1" title="True-cost $/hr is the punch-weighted employee wage over the last 90 days">wages 90d</span>}
                    </td>
                    <td className="text-end">
                      <Product minutes={r.minutes} rate={r.quotedRate} amount={r.price} implied={r.impliedQuoted}
                               rateNote={r.priceDerived ? 'price derived as laser subtotal − gas − consumables' : null} />
                      {r.priceDerived && <div className="small text-muted fst-italic">= laser subtotal − gas − consumables</div>}
                    </td>
                    <td className="text-end text-muted"><Product minutes={r.minutes} rate={r.trueRate} amount={r.cost} implied={r.impliedTrue} /></td>
                  </tr>
                );
              }
              if (r.kind === 'passthrough') {
                return (
                  <tr key={r.key}>
                    <td className="ps-3 text-muted fst-italic">{r.label}</td>
                    <td>{r.source ? <SourceBadge source={r.source} /> : <span className="text-muted small">pass-through</span>}</td>
                    <td className="text-end" title="Gas and consumables pass through at cost (no markup)">{fmtMoney(r.price, { dash: true, precise: r.precise })} <span className="text-muted small">(at cost)</span></td>
                    <td className="text-end text-muted">{fmtMoney(r.cost, { dash: true, precise: r.precise })}</td>
                  </tr>
                );
              }
              if (r.kind === 'outsourced') {
                return (
                  <tr key={r.key}>
                    <td className="fw-bold">{r.label}{r.owner === 'part' && <span className="badge bg-light text-dark border ms-1" title="This coating charge is priced on the part line">part-level</span>}</td>
                    <td><SourceBadge source={r.source} /> <span className="text-muted small">outsourced</span><MarkupSourceBadge source={r.markupSource} markup={r.markup} /></td>
                    <td className="text-end"><MarkupProduct cost={r.cost} markup={r.markup} price={r.price} implied={r.impliedMarkup} /></td>
                    <td className="text-end text-muted">{fmtMoney(r.cost, { dash: true })}</td>
                  </tr>
                );
              }
              if (r.kind === 'subtotal') {
                return (
                  <tr key={r.key} className="table-light">
                    <td className="fw-bold">{r.label}</td>
                    <td className="text-muted small">Σ laser rows</td>
                    <td className="text-end fw-bold">{fmtMoney(r.price, { dash: true })}</td>
                    <td className="text-end text-muted fw-bold">{fmtMoney(r.cost, { dash: true })}</td>
                  </tr>
                );
              }
              return null;
            })}
            {showRest && (
              <tr>
                <td className="text-muted fst-italic">Not itemised</td>
                <td className="text-muted small">engine total − Σ rows above</td>
                <td className="text-end text-muted">{Number.isFinite(restPrice) && Math.abs(restPrice) > 0.005 ? fmtMoney(restPrice) : '—'}</td>
                <td className="text-end text-muted">{Number.isFinite(restCost) && Math.abs(restCost) > 0.005 ? fmtMoney(restCost) : '—'}</td>
              </tr>
            )}
          </tbody>
          <tfoot style={{ borderTop: '2px solid var(--jq-border-medium, #adb5bd)' }}>
            <tr>
              <td className="fw-bold">TOTAL</td>
              <td className="text-muted small">Σ rows{Number.isFinite(a.profitMargin) ? ` · engine margin ${fmtPct(a.profitMargin)}` : ''}</td>
              <td className="text-end fw-bold" title="Calculated price = Σ priced rows">{fmtMoney(calcPrice, { dash: true })} <span className="text-muted small fw-normal">calculated</span></td>
              <td className="text-end fw-bold text-muted" title="True cost = Σ cost rows">{fmtMoney(a.totalCost, { dash: true })} <span className="small fw-normal">cost</span></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Price basis selection — sets price AND price_source/price_basis on the line */}
      {tm ? (
        <div className="small text-muted" data-testid="tm-cost-analysis-note">
          Time &amp; Materials line: no price basis. It is priced at completion on the Sales Order from its own
          shop time and material; the rows above are the estimate.
        </div>
      ) : (
      <div className="table-responsive">
        <table className="table table-sm mb-0" style={{ fontSize: '0.82rem' }}>
          <thead className="table-light">
            <tr>
              <th className="align-middle" style={{ width: '16%' }}>Price basis</th>
              {!isOps && <th className="text-end">Book (last saved)</th>}
              <th className="text-end">Calculated</th>
              {!isOps && lineOwnTarget && <th className="text-end">This line's target ({pctLabel(lineBasis)})</th>}
              {!isOps && <th className="text-end">Target margin (header {Number.isFinite(targetM) ? pctLabel(targetM) : '—'})</th>}
              <th className="text-end">Manual</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="align-top">
                <div className="small text-muted">Selected basis is saved with the line and re-applied on Calc / Recalculate.</div>
                {Number.isFinite(cost) && <div className="small text-muted">cost {fmtMoney(cost)}</div>}
              </td>
              {!isOps && (
                <PriceOption id={`${radioName}-book`} name={radioName} label="parts.price" price={bookPrice} cost={cost}
                  checked={source === 'book'} disabled={!canSelect}
                  onSelect={() => onSelectPrice({ price: bookPrice, price_source: 'book', price_basis: null })} />
              )}
              <PriceOption id={`${radioName}-calc`} name={radioName} label="Σ priced rows" price={calcPrice} cost={cost}
                checked={source === 'calculated'} disabled={!canSelect}
                onSelect={() => onSelectPrice({ price: calcPrice, price_source: 'calculated', price_basis: null })} />
              {!isOps && lineOwnTarget && (
                <PriceOption id={`${radioName}-own-target`} name={radioName}
                  label={`cost ÷ (1 − ${pctLabel(lineBasis)}) — saved on this line`}
                  price={ownTargetPrice} cost={cost} checked disabled={!canSelect}
                  onSelect={() => onSelectPrice({ price: ownTargetPrice, price_source: 'target_margin', price_basis: lineBasis })} />
              )}
              {!isOps && (
                <PriceOption id={`${radioName}-target`} name={radioName}
                  label={Number.isFinite(targetM) ? `cost ÷ (1 − ${pctLabel(targetM)})` : 'no margin set on the header'}
                  price={targetPrice} cost={cost} checked={source === 'target_margin' && !lineOwnTarget} disabled={!canSelect}
                  note={lineOwnTarget ? 'select to move this line to the header margin' : null}
                  onSelect={() => onSelectPrice({ price: targetPrice, price_source: 'target_margin', price_basis: targetM })} />
              )}
              <PriceOption id={`${radioName}-manual`} name={radioName} label={source === 'manual' || source === 'override_member' ? 'typed in the Price box' : 'type a price to switch'}
                price={source === 'manual' || source === 'override_member' ? current : NaN} cost={cost}
                checked={source === 'manual' || source === 'override_member'} disabled colorClass="text-primary" />
            </tr>
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
